import { useApolloClient, useLazyQuery } from "@apollo/client";

import { useAuthContext } from "../../../lib/context/Auth/AuthContext";

import { transport } from "../../grpc/grpcTransport";
import { PairingClient } from "../../grpc/pairing/pairing.client";
import { DeleteWorkplaceRequest, DeleteWorkplaceResponse } from "../workplaces";
import {
  AddDeviceResponse,
  AddDeviceVariables,
  ADD_DEVICE,
  DisconnectDeviceVariables,
  UpdateDeviceRoomVariables,
  DISCONNECT_DEVICE,
  UpdateDeviceVariables,
  UpdateDeviceResponse,
  UPDATE_DEVICE,
  UPDATE_EXISTING_DEVICE_ROOM,
  GetDevicesResponse,
  GET_DEVICES,
  UNPAIR_DEVICE,
  UpdateDeviceWayfinderVariables,
  UPDATE_EXISTING_DEVICE_WAY_FINDER,
  DISCONNECT_DEVICE_WAY_FINDER,
  GetAllDevicesRequest,
} from "./devices";
import { GET_ROOM } from "../rooms/rooms";

import { PairingDeviceType } from "../../grpc/pairing/pairing";

export const useDevicesRequests = () => {
  const client = useApolloClient();
  const { tokenInterceptor } = useAuthContext();
  const pairingClient = new PairingClient(transport);
  const [requestDevices, requestState] = useLazyQuery<
    GetDevicesResponse,
    GetAllDevicesRequest
  >(GET_DEVICES, {
    fetchPolicy: "cache-and-network",
  });

  const addDevice = async (variables: AddDeviceVariables) => {
    return await client.mutate<AddDeviceResponse, AddDeviceVariables>({
      mutation: ADD_DEVICE,
      variables,
      update: (cache) => {
        if (variables.room?.id) {
          cache.evict({ id: `Room:${variables.room.id}`, fieldName: "devices" });
        }

        if (variables.wayFinder?.id) {
          cache.evict({ id: `Wayfinder:${variables.wayFinder.id}`, fieldName: "devices" });
        }

        cache.gc();
      },
    });
  };

  const updateDevice = async (variables: UpdateDeviceVariables) => {
    return await client.mutate<UpdateDeviceResponse, UpdateDeviceVariables>({
      mutation: UPDATE_DEVICE,
      variables,
    });
  };

  const unpairDevice = async (variables: DeleteWorkplaceRequest) => {
    return await client.mutate<DeleteWorkplaceResponse, DeleteWorkplaceRequest>(
      {
        mutation: UNPAIR_DEVICE,
        variables,
      }
    );
  };

  const updateDeviceWayfinder = async (
    variables: UpdateDeviceWayfinderVariables
  ) => {
    const { oldWayfinderId, ...rest } = variables;
    return await client.mutate<
      UpdateDeviceResponse,
      UpdateDeviceWayfinderVariables
    >({
      mutation: UPDATE_EXISTING_DEVICE_WAY_FINDER,
      variables: rest,
      update: (cache, { data }) => {
        if (oldWayfinderId) {
          cache.evict({
            id: `Wayfinder:${oldWayfinderId}`,
            fieldName: "devices",
          });
        }

        if (variables.wayFinderId) {
          cache.evict({
            id: `Wayfinder:${variables.wayFinderId}`,
            fieldName: "devices",
          });
        }

        cache.gc();
      },
    });
  };

  const updateDeviceRoom = async (variables: UpdateDeviceRoomVariables) => {
    const { oldRoomId, ...rest } = variables;

    return await client.mutate<UpdateDeviceResponse, UpdateDeviceRoomVariables>(
      {
        mutation: UPDATE_EXISTING_DEVICE_ROOM,
        variables: rest,
        update: (cache, { data }) => {
          if (oldRoomId) {
            cache.evict({ id: `Room:${oldRoomId}`, fieldName: "devices" });
          }

          if (variables.roomId) {
            cache.evict({
              id: `Room:${variables.roomId}`,
              fieldName: "devices",
            });
          }

          cache.gc();
        },
      }
    );
  };

  const disconnectDevice = async (variables: DisconnectDeviceVariables) => {
    return await client.mutate<any, DisconnectDeviceVariables>({
      mutation: DISCONNECT_DEVICE,
      variables,
      refetchQueries: variables.roomId
        ? [
            {
              query: GET_ROOM,
              variables: { id: variables.roomId },
            },
          ]
        : [],
      update: (cache) => {
        if (variables.roomId) {
          cache.evict({
            id: `Room:${variables.roomId}`,
            fieldName: "devices",
          });
        }

        // Evict the device's room field so it shows as unassigned
        cache.evict({ id: `Device:${variables.id}`, fieldName: "room" });

        cache.gc();
      },
    });
  };

  const disconnectDeviceWayfinder = async (
    variables: DisconnectDeviceVariables
  ) => {
    return await client.mutate<any, DisconnectDeviceVariables>({
      mutation: DISCONNECT_DEVICE_WAY_FINDER,
      variables,

      update: (cache) => {
        if (variables.wayFinderId) {
          cache.evict({
            id: `Wayfinder:${variables.wayFinderId}`,
            fieldName: "devices",
          });
        }

        // Evict the device's wayFinder field so it shows as unassigned
        cache.evict({ id: `Device:${variables.id}`, fieldName: "wayFinder" });

        cache.gc();
      },
    });
  };

  const pairDevice = async (
    code: string,
    customerId: string,
    deviceType: PairingDeviceType
  ) => {
    return await pairingClient.pair(
      { code, customerId, deviceType },
      {
        interceptors: [tokenInterceptor],
      }
    );
  };

  return {
    addDevice,
    getDevices: {
      state: requestState,
      request: requestDevices,
    },
    pairDevice,
    updateDevice,
    unpairDevice,
    disconnectDevice,
    disconnectDeviceWayfinder,
    updateDeviceRoom,
    updateDeviceWayfinder,
  };
};
